import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Bell, Menu, X } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import ChatWidget from "@/components/ChatWidget";

export function AppShell({ nav, children, title }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [notifs, setNotifs] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [open, setOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const unread = notifs.filter((n) => !n.read).length;

  const loadNotifs = async () => {
    try { const { data } = await api.get("/notifications"); setNotifs(data); } catch {}
    if (user?.role === "admin") {
      try {
        const { data } = await api.get("/chat/messages");
        setUnreadMessages(data.filter(m => !m.read_by_admin).length);
      } catch {}
    }
  };
  // apply persisted service colors globally
  useEffect(() => {
    api.get("/services").then(r => {
      (r.data || []).forEach(s => {
        document.documentElement.style.setProperty(`--svc-${s.key}`, s.color);
      });
      // custom services have no static .svc-<key> class in index.css: generate it
      let tag = document.getElementById("svc-dynamic-styles");
      if (!tag) { tag = document.createElement("style"); tag.id = "svc-dynamic-styles"; document.head.appendChild(tag); }
      const builtin = ["rieducazione_motoria", "small_group", "valutazione_iniziale", "coaching_online", "personal_training"];
      tag.textContent = (r.data || []).filter(s => !builtin.includes(s.key))
        .map(s => `.svc-${s.key} { background: var(--svc-${s.key}); color: #FFFFFF; }`).join("\n");
    }).catch(() => {});
  }, []);
  useEffect(() => { loadNotifs(); const t = setInterval(loadNotifs, 20000); return () => clearInterval(t); }, []);

  // External components (e.g. ChatWidget) can request a bell refresh after they mark items read.
  useEffect(() => {
    const h = () => loadNotifs();
    window.addEventListener("notifications:refresh", h);
    return () => window.removeEventListener("notifications:refresh", h);
    // eslint-disable-next-line
  }, []);

  const handleLogout = async () => { await logout(); navigate("/login"); };
  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="min-h-screen flex text-white">
      {/* Sidebar */}
      <aside className={`app-sidebar w-64 shrink-0 border-r border-white/10 bg-[#08040C] flex flex-col ${sidebarOpen ? "open" : ""}`}>
        <div className="p-6 border-b border-white/10 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <img
              src="/logo.png"
              alt="Sara Absi · Chinesiologa Clinica & Personal Trainer"
              data-testid="brand-logo"
              className="h-10 w-auto object-contain bg-white rounded-sm p-1"
            />
            <div className="label-caps mt-2 text-white/40">{user?.role === "admin" ? "Trainer studio" : "Cliente"}</div>
          </div>
          <button onClick={closeSidebar} className="md:hidden text-white/60"><X size={20} /></button>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {nav.map((n) => {
            const badgeCount = n.key === "messages" ? unreadMessages : 0;
            return (
            <NavLink
              key={n.to} to={n.to}
              data-testid={`nav-${n.key}`}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 border-l-2 font-heading uppercase tracking-wider text-sm transition-colors relative ${
                  isActive ? "border-[#A855F7] bg-white/5 text-white" : "border-transparent text-white/60 hover:text-white hover:bg-white/5"
                }`
              }
              end={n.end}
            >
              {n.icon}
              <span>{n.label}</span>
              {badgeCount > 0 && (
                <span data-testid={`nav-badge-${n.key}`} className="ml-auto min-w-[18px] h-[18px] px-1 text-[10px] font-bold flex items-center justify-center bg-[#FF3366] text-white">{badgeCount}</span>
              )}
            </NavLink>
          );})}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-xs text-white/50 mb-2">{user?.name}</div>
          <div className="text-xs text-white/30 mb-4 truncate">{user?.email}</div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="w-full btn-ghost flex items-center justify-center gap-2 !py-2 !text-xs"
          >
            <LogOut size={14} /> Esci
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="app-main flex-1 min-w-0">
        <header className="h-16 border-b border-white/10 px-4 md:px-8 flex items-center justify-between sticky top-0 bg-[#08040C] z-30 gap-2">
          <button onClick={() => setSidebarOpen(true)} className="md:hidden text-white/70" data-testid="hamburger-btn">
            <Menu size={22} />
          </button>
          <h1 className="font-heading font-bold uppercase tracking-tight text-base md:text-lg truncate">{title}</h1>
          <div className="relative">
            <button
              data-testid="notif-bell"
              onClick={() => setOpen(!open)}
              className="relative w-10 h-10 border border-white/10 flex items-center justify-center hover:border-[#A855F7] transition-colors"
            >
              <Bell size={16} />
              {unread > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 text-[10px] font-bold flex items-center justify-center bg-[#FF3366] text-white animate-pulse">
                  {unread}
                </span>
              )}
            </button>
            {open && (
              <div className="absolute right-0 mt-2 w-80 md:w-96 bg-[#120B19] border border-white/10 z-50 max-h-[420px] overflow-auto">
                <div className="p-3 border-b border-white/10 flex items-center justify-between">
                  <div className="label-caps">Notifiche</div>
                  <button data-testid="notifications-read-all" onClick={async () => { await api.post("/notifications/read-all"); loadNotifs(); }}
                    className="text-[11px] text-[#A855F7] hover:underline">Segna tutte come lette</button>
                </div>
                {notifs.length === 0 && <div className="p-6 text-sm text-white/40">Nessuna notifica</div>}
                {notifs.map((n) => (
                  <div key={n.id} className={`p-3 border-b border-white/5 text-sm ${n.read ? "text-white/40" : "text-white"}`}>
                    <div>{n.message}</div>
                    <div className="text-[10px] text-white/30 mt-1">{new Date(n.created_at).toLocaleString("it-IT")}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </header>
        <div className="p-4 pb-28 md:p-8 md:pb-28">{children}</div>
      </main>

      <ChatWidget role={user?.role} />
    </div>
  );
}
